import { APP_RIDE_SELECT, isFutureScheduledPickup, mapAppRide } from './rides';

const SCHEDULE_MATCH_LEAD_MS = 15 * 60 * 1000;

export const DAILY_RANGE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export const DAILY_GROUP_OPTIONS = [
  { value: 'day', label: 'By day' },
  { value: 'week', label: 'By week' },
  { value: 'month', label: 'By month' },
];

export function isDailyProduct(ride) {
  return String(ride?.product || '').toLowerCase() === 'daily';
}

/** Daily city ride booked for now (not a future scheduled pickup). */
export function isInstantDailyRide(ride, now = Date.now()) {
  if (!isDailyProduct(ride)) return false;
  const status = String(ride?.status || '').toLowerCase();
  const pickupAt = ride?.scheduled_at || ride?.raw?.pickup_at;
  const requestedAt = ride?.created_at || ride?.raw?.requested_at;

  if (status === 'scheduled' && isFutureScheduledPickup(pickupAt, now)) {
    return false;
  }
  if (pickupAt && requestedAt) {
    const lead = Date.parse(pickupAt) - Date.parse(requestedAt);
    if (Number.isFinite(lead) && lead > SCHEDULE_MATCH_LEAD_MS) {
      return false;
    }
  }
  return true;
}

export function dailyRideLifecycleBucket(ride) {
  const board = ride?.boardStatus;
  if (board === 'cancelled') return 'cancelled';
  if (board === 'completed') return 'completed';
  return 'active';
}

export function resolveRangePreset(preset, now = Date.now()) {
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  switch (preset) {
    case 'today':
      return { start: start.getTime(), end: end.getTime() };
    case '7d':
      start.setDate(start.getDate() - 6);
      return { start: start.getTime(), end: end.getTime() };
    case '30d':
      start.setDate(start.getDate() - 29);
      return { start: start.getTime(), end: end.getTime() };
    case '90d':
      start.setDate(start.getDate() - 89);
      return { start: start.getTime(), end: end.getTime() };
    default:
      start.setDate(start.getDate() - 29);
      return { start: start.getTime(), end: end.getTime() };
  }
}

function startOfWeekMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + offset);
  return d;
}

export function periodBucketKey(isoDate, groupBy) {
  const d = new Date(isoDate);
  if (!Number.isFinite(d.getTime())) return 'unknown';
  if (groupBy === 'month') {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
  if (groupBy === 'week') {
    return startOfWeekMonday(d).toISOString().slice(0, 10);
  }
  return d.toISOString().slice(0, 10);
}

export function formatPeriodLabel(key, groupBy) {
  if (key === 'unknown') return 'Unknown date';
  if (groupBy === 'month') {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-IN', {
      month: 'short',
      year: 'numeric',
    });
  }
  if (groupBy === 'week') {
    const start = new Date(key);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const a = start.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    const b = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    return `Week ${a} – ${b}`;
  }
  return new Date(key).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function summarizeInstantDailyRides(rides, { rangeStart, rangeEnd, groupBy = 'day' } = {}) {
  const instant = rides.filter((r) => isInstantDailyRide(r));
  const snapshot = { active: 0, completed: 0, cancelled: 0, total: instant.length };

  instant.forEach((ride) => {
    const bucket = dailyRideLifecycleBucket(ride);
    snapshot[bucket] += 1;
  });

  const periodMap = new Map();
  instant.forEach((ride) => {
    const created = ride.created_at || ride.raw?.requested_at;
    if (!created) return;
    const t = Date.parse(created);
    if (rangeStart != null && t < rangeStart) return;
    if (rangeEnd != null && t > rangeEnd) return;

    const key = periodBucketKey(created, groupBy);
    if (!periodMap.has(key)) {
      periodMap.set(key, { key, active: 0, completed: 0, cancelled: 0, total: 0 });
    }
    const row = periodMap.get(key);
    const lifecycle = dailyRideLifecycleBucket(ride);
    row[lifecycle] += 1;
    row.total += 1;
  });

  const periods = [...periodMap.values()].sort((a, b) => b.key.localeCompare(a.key));

  return { instant, snapshot, periods };
}

export async function fetchDailyRideRequests(
  supabaseClient,
  { limit = 800, requestedSince = null, requestedBefore = null } = {}
) {
  if (!supabaseClient) {
    return { rides: [], error: 'Supabase is not configured.' };
  }

  const run = (selectColumns) => {
    let query = supabaseClient
      .from('ride_requests')
      .select(selectColumns)
      .ilike('product', 'daily')
      .order('requested_at', { ascending: false })
      .limit(limit);

    if (requestedSince != null) {
      query = query.gte('requested_at', new Date(requestedSince).toISOString());
    }
    if (requestedBefore != null) {
      query = query.lte('requested_at', new Date(requestedBefore).toISOString());
    }
    return query;
  };

  let result = await run(APP_RIDE_SELECT);
  if (result.error && /distance_km/i.test(result.error.message || '')) {
    result = await run(
      'id,rider_name,rider_phone,pickup,dropoff,pickup_at,requested_at,status,driver_id,driver_name,vehicle_number,vehicle_name,car_name,fare,payment,product,admin_note'
    );
  }

  if (result.error) {
    const message = result.error.message || 'Could not load daily rides.';
    const hint =
      result.error.code === '42501' || /permission|row-level/i.test(message)
        ? ' Run supabase/fix-ride-board-app-bookings.sql in Supabase.'
        : '';
    return { rides: [], error: message + hint };
  }

  const rides = (result.data || []).map(mapAppRide);
  return { rides, error: '' };
}
